/**
 * @fileoverview Blueprint & Floor Plan Icon Set - Specialized icons for map editors
 * @module icons/blueprint-icons
 * @version 1.0.0
 *
 * @description
 * A curated set of blueprint and floor plan icons for use in map editors and
 * architectural drawing tools. These icons represent common floor plan elements
 * and drawing tools (doors, windows, stairs, walls, etc.) as well as utility
 * tools (compass, grid, dimension lines).
 *
 * Usage:
 *   import '@cliftontechlabs/ui-kit/icons';
 *   <ctl-icon name="floor-plan"></ctl-icon>
 *
 * All icons are 24x24 viewBox and use currentColor for theming.
 */

export const BLUEPRINT_ICONS = {
  // ── Orientation & Layout ────────────────────────────────────────────────

  /**
   * compass – Four-pointed compass rose (N/S/E/W arrows).
   * Use as a north-indicator or orientation control in map views.
   */
  "compass": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l3 9H9l3-9zm0 20-3-9h6l-3 9zM2 12l9-3v6L2 12zm20 0-9 3V9l9 3z"/>
    </svg>
  `,

  /**
   * floor-plan – Top-down multi-room floor plan overview.
   * Represents a full building layout; useful as a "floor plan view" toggle icon.
   */
  "floor-plan": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v2H3zm0 16h18v2H3zm0-16h2v18H3zm16 0h2v18h-2zM12 5h2v14h-2zM5 12h7v2H5z"/>
    </svg>
  `,

  /**
   * blueprint – Document with horizontal rule lines, styled as a blueprint sheet.
   * Useful as a general "blueprint" or "drawing" document indicator.
   */
  "blueprint": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 2h12l4 4v16H4V2zm2 2v16h12V7h-3a1 1 0 0 1-1-1V3H6zm8 0v3h3l-3-3zM8 9h8v2H8zm0 4h8v2H8zm0 4h6v2H8z"/>
    </svg>
  `,

  // ── Floor Plan Elements ─────────────────────────────────────────────────

  /**
   * door – Architectural door symbol (left jamb + horizontal door leaf + quarter-circle swing arc).
   * Standard top-down floor plan door representation.
   */
  "door": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 3v18h2V3zm2 1h9v2H6zm9-1a9 9 0 0 1-9 9v-2a7 7 0 0 0 7-7z"/>
    </svg>
  `,

  /**
   * window – Architectural window symbol (frame with two panes divided by a horizontal bar).
   * Standard top-down floor plan window representation.
   */
  "window": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4h16v2H4zm0 14h16v2H4zm0-14h2v16H4zm14 0h2v16h-2zM4 11h16v2H4z"/>
    </svg>
  `,

  /**
   * stairs – Side-profile staircase shape (ascending steps from left to right).
   * Use to indicate a staircase element in a floor plan.
   */
  "stairs": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 21v-4h4v-4h4v-4h4v-4h6v16H3z"/>
    </svg>
  `,

  /**
   * room – Single room outline (thick rectangular border).
   * Represents a room or space boundary in a floor plan.
   */
  "room": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v2H3zm0 16h18v2H3zm0-16h2v18H3zm16 0h2v18h-2z"/>
    </svg>
  `,

  // ── Drawing & Measurement Tools ─────────────────────────────────────────

  /**
   * wall – Thick horizontal wall segment.
   * Use as the "draw wall" tool indicator in a map editor toolbar.
   */
  "wall": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 9h20v6H2z"/>
    </svg>
  `,

  /**
   * grid – 3×3 grid of intersecting lines.
   * Use to toggle snap-to-grid or background grid display in a map editor.
   */
  "grid": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h2v18H3zm8 0h2v18h-2zm8 0h2v18h-2zM3 3h18v2H3zm0 8h18v2H3zm0 8h18v2H3z"/>
    </svg>
  `,

  /**
   * dimension – Dimension/measurement line with arrows at both ends and extension lines.
   * Use as the "measure" or "dimension" tool in a map editor toolbar.
   */
  "dimension": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h2v18H6zm10 0h2v18h-2zM5 12l4-3v2h6v-2l4 3-4 3v-2H9v2z"/>
    </svg>
  `,
};
